import { useMemo } from 'react';
import { safeDecimal } from '@/helpers/number';
import { useGetBrandsForInvestment } from '@/query/queries/investing/useGetBrandsForInvestment';

/**
 * React hook it fetches brands via `useGetBrandsForInvestment(step)` and computes
 * multiple aggregated metrics such as selected/filled counts, capacity totals,
 * fee calculations, and helper lists for UI logic.
 *
 * @param {string} [props.step] - Current investment step used to fetch brands.
 * @param {number} [props.totalAllocated] - Total allocated amount used to calculate fees.
 *
 * @returns {data} Object - Raw API response from `useGetBrandsForInvestment`.
 * @returns {isLoading} boolean - Indicates whether brand data is being loaded.
 * @returns {hasData} boolean - Indicates whether brand data is available.
 *
 * @returns {newBrands} number - Count of brands marked as new.
 * @returns {filledBrandCount} number - Count of filled brands.
 * @returns {selectedBrandCount} number - Count of selected brands.
 *
 * @returns {newBrandList} Object[] - Brands that are marked as new.
 * @returns {selectedAndFilledBrandList} Object[] - Brands that are both selected and filled.
 * @returns {selectedAndUnFilledBrandList} Object[] - Brands that are selected but not filled.
 * @returns {filledBrandList} Object[] - All filled brands.
 * @returns {selectedBrandList} Object[] - All selected brands.
 * @returns {availableForSelectionBrandList} Object[] - Brands available for selection.
 *
 * @returns {capacity} Object - Aggregated capacity values.
 * @returns {capacity.conservative} number - Total conservative capacity.
 * @returns {capacity.moderate} number - Total moderate capacity.
 * @returns {totalCapacity} number - Sum of conservative and moderate capacities.
 *
 * @returns {totalCount} number - Total number of brands.
 * @returns {totalAvailableCount} number - Number of brands available for selection.
 * @returns {excludedBrands} number - Count of excluded brands.
 *
 * @returns {selectedAndFilledBrandCount} number - Count of selected and filled brands.
 * @returns {selectedAndUnFilledBrandCount} number - Count of selected but unfilled brands.
 *
 * @returns {isAllSelected} boolean - Whether all brands are selected.
 * @returns {isAllAvailableSelected} boolean - Whether all available brands are selected.
 *
 * @returns {initialFee} number - Initial fee percentage from API.
 * @returns {fee} number - Effective fee percentage after exclusions.
 * @returns {feeAmount} number - Calculated fee amount based on allocation.
 * @returns {confirmedFeeAmount} number - Confirmed fee amount from API.
 */

export const useBrandSelectionStatus = (props = {}) => {
  const { step, totalAllocated } = props;
  const { data, isLoading } = useGetBrandsForInvestment(step);

  const parsedData = useMemo(() => {
    const initial = {
      newBrands: 0,
      filledBrandCount: 0,
      selectedBrandCount: 0,
      selectedBrandList: [],
      newBrandList: [],
      selectedAndFilledBrandList: [],
      selectedAndUnFilledBrandList: [],
      filledBrandList: [],
      availableForSelectionBrandList: [],
      capacity: {
        conservative: 0,
        moderate: 0,
      },
    };

    const summary =
      data?.list.reduce((acc, brand) => {
        if (brand.is_new) {
          acc.newBrands++;
          acc.newBrandList.push(brand);
        }

        if (brand.selected) {
          acc.selectedBrandCount++;
          acc.selectedBrandList.push(brand);

          if (brand.is_filled) {
            acc.selectedAndFilledBrandList.push(brand);
          } else {
            acc.selectedAndUnFilledBrandList.push(brand);
          }
        }

        if (brand.is_filled) {
          acc.filledBrandList.push(brand);
        }

        if (!brand.is_filled) {
          acc.availableForSelectionBrandList.push(brand);
        }

        acc.capacity.conservative = safeDecimal(brand.capacity.conservative)
          .add(safeDecimal(acc.capacity.conservative))
          .toNumber();

        acc.capacity.moderate = safeDecimal(brand.capacity.moderate)
          .add(safeDecimal(acc.capacity.moderate))
          .toNumber();

        return acc;
      }, initial) || initial;

    const totalCount = data?.list.length || 0;
    const excludedBrands = data?.excluded || 0;
    const isAllSelected = totalCount === summary.selectedBrandCount;
    const isAllAvailableSelected =
      summary.availableForSelectionBrandList.length ===
      summary.selectedBrandCount;

    const feeAmount =
      data && totalAllocated && excludedBrands
        ? (totalAllocated * data?.fee) / 100
        : 0;

    summary.initialFee = data?.fee || 0;
    summary.fee = excludedBrands ? data?.fee : 0;
    summary.confirmedFeeAmount = data?.fee_amount;
    summary.feeAmount = feeAmount;
    summary.totalCount = totalCount;
    summary.totalAvailableCount =
      summary.availableForSelectionBrandList.length || 0;
    summary.excludedBrands = excludedBrands;
    summary.filledBrandCount = summary.filledBrandList.length;
    summary.selectedAndFilledBrandCount =
      summary.selectedAndFilledBrandList.length;
    summary.selectedAndUnFilledBrandCount =
      summary.selectedAndUnFilledBrandList.length;
    summary.isAllSelected = isAllSelected;
    summary.isAllAvailableSelected = isAllAvailableSelected;
    summary.totalCapacity = safeDecimal(summary.capacity.conservative)
      .add(safeDecimal(summary.capacity.moderate))
      .toNumber();

    return summary;
  }, [data]);

  return {
    data,
    isLoading,
    hasData: !!data,
    ...parsedData,
  };
};
